import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { pbdqFont } from '../theme/pbdqTokens';
import PbdqImageLightboxWrapper from './PbdqImageLightboxWrapper.comp';

const useStyles = makeStyles(() => ({
    card: {
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 5fr) minmax(0, 6fr)',
        alignItems: 'stretch',
        border: '1px solid #E6E9EE',
        borderRadius: 24,
        overflow: 'hidden',
        background: '#FFFFFF',
        boxSizing: 'border-box',
        '@media (max-width: 900px)': {
            gridTemplateColumns: '1fr',
        },
    },
    imageWrap: {
        position: 'relative',
        minHeight: 360,
        background: '#D9DFE7',
        borderRight: '1px solid #CFD6DF',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        '@media (max-width: 900px)': {
            minHeight: 280,
            borderRight: 'none',
            borderBottom: '1px solid #CFD6DF',
        },
    },
    lightboxWrap: {
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 0,
    },
    image: {
        width: '100%',
        height: '100%',
        objectFit: 'contain',
        display: 'block',
    },
    logoBadge: {
        position: 'absolute',
        left: 20,
        top: 20,
        display: 'inline-flex',
        alignItems: 'center',
        background: '#FFFFFF',
        borderRadius: 14,
        padding: '14px 20px',
        boxShadow: '0 12px 32px rgba(23, 41, 57, 0.18)',
        zIndex: 2,
        gap: 12,
        boxSizing: 'border-box',
    },
    logoImg: {
        display: 'block',
        height: 64,
        width: 'auto',
        maxWidth: 240,
        objectFit: 'contain',
    },
    body: {
        minWidth: 0,
        padding: 'clamp(24px, 3vw, 36px)',
        display: 'grid',
        gap: 14,
        alignContent: 'center',
        boxSizing: 'border-box',
    },
    sectorTag: {
        display: 'inline-flex',
        width: 'max-content',
        alignItems: 'center',
        gap: 8,
        padding: '6px 12px',
        borderRadius: 999,
        background: '#FFFFFF',
        border: '1px solid #E6E9EE',
        fontSize: 11.5,
        fontWeight: 700,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        color: '#1B2430',
        whiteSpace: 'nowrap',
        fontFamily: pbdqFont,
    },
    sectorDot: {
        width: 7,
        height: 7,
        borderRadius: '50%',
        flexShrink: 0,
    },
    title: {
        fontSize: 'clamp(22px, 2.3vw, 30px)',
        lineHeight: 1.15,
        letterSpacing: '-0.02em',
        fontWeight: 800,
        margin: 0,
        maxWidth: '30ch',
        color: '#1B2430',
        fontFamily: pbdqFont,
    },
    summary: {
        fontSize: 15,
        lineHeight: 1.65,
        margin: 0,
        color: '#5B6470',
        maxWidth: '70ch',
        whiteSpace: 'pre-line',
        fontFamily: pbdqFont,
    },
    metrics: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 150px), 1fr))',
        gap: 10,
    },
    metric: {
        padding: '14px 16px',
        border: '1px solid #E6E9EE',
        borderRadius: 12,
        background: '#FFFFFF',
        display: 'grid',
        gap: 4,
        alignContent: 'start',
        boxSizing: 'border-box',
    },
    metricValue: {
        fontSize: 20,
        lineHeight: 1.1,
        fontWeight: 800,
        letterSpacing: '-0.02em',
        margin: 0,
        color: '#A63C0C',
        fontVariantNumeric: 'tabular-nums',
        fontFamily: pbdqFont,
    },
    metricLabel: {
        fontSize: 12.5,
        lineHeight: 1.4,
        margin: 0,
        color: '#5B6470',
        fontFamily: pbdqFont,
    },
    quoteBox: {
        margin: 0,
        padding: '14px 18px',
        borderLeft: '3px solid #ED6628',
        background: '#FFFFFF',
        borderRadius: '0 12px 12px 0',
        display: 'grid',
        gap: 6,
    },
    quoteText: {
        fontSize: 14.5,
        lineHeight: 1.55,
        fontWeight: 500,
        margin: 0,
        color: '#1B2430',
        fontFamily: pbdqFont,
    },
    quoteAttr: {
        fontSize: 12.5,
        color: '#6B7280',
        fontFamily: pbdqFont,
    },
    footerRow: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '10px 20px',
        alignItems: 'center',
        marginTop: 4,
    },
    linkBtn: {
        display: 'inline-flex',
        alignItems: 'center',
        height: 44,
        padding: '0 18px',
        borderRadius: 999,
        border: '1.5px solid #ED6628',
        background: '#FFFFFF',
        color: '#A63C0C',
        textDecoration: 'none',
        fontSize: 14,
        fontWeight: 700,
        whiteSpace: 'nowrap',
        fontFamily: pbdqFont,
        transition: 'background 180ms ease, color 180ms ease',
        '&:hover': {
            background: '#ED6628',
            color: '#FFFFFF',
            textDecoration: 'none',
        },
    },
    builtWith: {
        fontSize: 13,
        color: '#5B6470',
        fontFamily: pbdqFont,
    },
    builtWithStrong: {
        color: '#6B7280',
        fontWeight: 600,
    },
}));

const PbdqImpactStoryCard = ({
    hero,
    heroAlt,
    heroFit,
    heroPosition,
    logo,
    logoAlt,
    logo2,
    logo2Alt,
    logoScale,
    logoScale2,
    logoStyle,
    logoStyle2,
    sectorLabel,
    sectorColor,
    title,
    summary,
    body,
    quote,
    stats,
    builtWith,
    websiteUrl,
}) => {
    const classes = useStyles();

    const storyAlt = heroAlt && heroAlt !== 'Feature photo' ? heroAlt : title;
    const hasValidQuote = quote && quote.text && !quote.text.startsWith('[Placeholder');

    return (
        <article className={classes.card} data-story="">
            <div className={classes.imageWrap}>
                {hero && (
                    <PbdqImageLightboxWrapper
                        src={hero}
                        alt={storyAlt}
                        caption={storyAlt}
                        className={classes.lightboxWrap}
                    >
                        <img
                            loading="lazy"
                            src={hero}
                            alt={storyAlt}
                            className={classes.image}
                            style={{
                                objectFit: heroFit || 'contain',
                                objectPosition: heroPosition || 'center center',
                            }}
                        />
                    </PbdqImageLightboxWrapper>
                )}
                <span className={classes.logoBadge}>
                    {logo && (
                        <img
                            loading="lazy"
                            src={logo}
                            alt={logoAlt || 'Logo'}
                            className={classes.logoImg}
                            style={{
                                transform: logoScale ? `scale(${logoScale})` : undefined,
                                transformOrigin: 'center center',
                                ...logoStyle,
                            }}
                        />
                    )}
                    {logo2 && (
                        <img
                            loading="lazy"
                            src={logo2}
                            alt={logo2Alt || 'Logo 2'}
                            className={classes.logoImg}
                            style={{
                                transform: logoScale2 ? `scale(${logoScale2})` : undefined,
                                transformOrigin: 'center center',
                                ...logoStyle2,
                            }}
                        />
                    )}
                </span>
            </div>

            <div className={classes.body}>
                <span className={classes.sectorTag}>
                    <span className={classes.sectorDot} style={{ background: sectorColor }} />
                    {sectorLabel}
                </span>

                <h3 className={classes.title}>{title}</h3>

                <p className={classes.summary}>{summary || body}</p>

                {stats && stats.length > 0 && (
                    <div className={classes.metrics}>
                        {stats.slice(0, 3).map((stat) => (
                            <div key={stat.label} className={classes.metric}>
                                <p className={classes.metricValue}>{stat.value}</p>
                                <p className={classes.metricLabel}>{stat.label}</p>
                            </div>
                        ))}
                    </div>
                )}

                {hasValidQuote && (
                    <blockquote className={classes.quoteBox}>
                        <p className={classes.quoteText}>“{quote.text}”</p>
                        {quote.attribution && (
                            <footer className={classes.quoteAttr}>— {quote.attribution}</footer>
                        )}
                    </blockquote>
                )}

                <div className={classes.footerRow}>
                    {websiteUrl && (
                        <a
                            href={websiteUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={classes.linkBtn}
                        >
                            Read the story →
                        </a>
                    )}
                    {builtWith && (
                        <span className={classes.builtWith}>
                            <strong className={classes.builtWithStrong}>Built with</strong> · {builtWith}
                        </span>
                    )}
                </div>
            </div>
        </article>
    );
};

export default PbdqImpactStoryCard;
